import { MapPin } from "lucide-react";

export interface GeofenceMapPreviewProps {
  /** Centre latitude (decimal degrees). Used for label only — the map is static. */
  latitude: number;
  /** Centre longitude (decimal degrees). Used for label only — the map is static. */
  longitude: number;
  /** Radius in metres. Scaled visually inside the preview. */
  radiusMeters: number;
  /** Where the value came from. Drives the label above the map. */
  source: "site" | "override";
  /** Optional name of the parent site for the source label. */
  siteName?: string;
  /** Optional disabled visual treatment (used when "Use Site geofence" is on). */
  disabled?: boolean;
}

/**
 * Static map preview with an SVG circle overlay.
 *
 * V1 prototype only — no live map SDK. Renders a gridded placeholder tile
 * and an SVG circle whose radius is computed from the input radius (metres),
 * clamped so the geofence reads as a sensible diameter even at extreme values.
 * The label above the map reads "Effective geofence — From Site: <name>" or
 * "Effective geofence — Overridden".
 */
export function GeofenceMapPreview({
  latitude,
  longitude,
  radiusMeters,
  source,
  siteName,
  disabled = false,
}: GeofenceMapPreviewProps) {
  // The preview is 320px wide × 200px tall. Radius rendered between 24px (min)
  // and 88px (max) so a 50m radius looks small and a 500m radius looks large
  // without overflowing the preview tile.
  const minPx = 24;
  const maxPx = 88;
  const minM = 25;
  const maxM = 500;
  const clampedM = Math.max(minM, Math.min(maxM, radiusMeters));
  const radiusPx = minPx + ((clampedM - minM) / (maxM - minM)) * (maxPx - minPx);

  const sourceLabel =
    source === "site"
      ? `Effective geofence — From Site${siteName ? `: ${siteName}` : ""}`
      : "Effective geofence — Overridden";

  return (
    <div className={`flex h-full flex-col ${disabled ? "opacity-60" : ""}`}>
      <div className="flex items-center gap-xxs text-xs text-greyscale-text-caption mb-xxs">
        <MapPin size={12} aria-hidden="true" />
        <span>{sourceLabel}</span>
      </div>
      <div
        className="relative w-full flex-1 min-h-[240px] rounded-md overflow-hidden border border-greyscale-border bg-neutral-50"
        role="img"
        aria-label={`Geofence preview centred at ${latitude.toFixed(4)}, ${longitude.toFixed(4)}, radius ${radiusMeters} metres`}
      >
        {/* Grid background to imply a map tile without a live SDK */}
        <svg
          viewBox="0 0 320 200"
          xmlns="http://www.w3.org/2000/svg"
          className="absolute inset-0 w-full h-full"
          aria-hidden="true"
          preserveAspectRatio="none"
        >
          <defs>
            <pattern id="map-grid" width="20" height="20" patternUnits="userSpaceOnUse">
              <path
                d="M 20 0 L 0 0 0 20"
                fill="none"
                stroke="#E4E7EB"
                strokeWidth="1"
              />
            </pattern>
          </defs>
          <rect width="320" height="200" fill="url(#map-grid)" />
          {/* Faux road overlays */}
          <path d="M 0 60 Q 80 70 160 60 T 320 80" stroke="#CBD2D9" strokeWidth="3" fill="none" />
          <path d="M 0 140 Q 90 130 180 145 T 320 130" stroke="#CBD2D9" strokeWidth="3" fill="none" />
          <path d="M 80 0 L 90 200" stroke="#CBD2D9" strokeWidth="2" fill="none" />
          <path d="M 220 0 L 200 200" stroke="#CBD2D9" strokeWidth="2" fill="none" />

          {/* Geofence circle — pink fill with darker stroke per jem-ui token vibe */}
          <circle
            cx="160"
            cy="100"
            r={radiusPx}
            fill="rgba(244, 114, 182, 0.18)"
            stroke="#DB2777"
            strokeWidth="2"
          />
          {/* Centre pin marker */}
          <circle cx="160" cy="100" r="4" fill="#DB2777" />
        </svg>
      </div>
      <div className="mt-xxs text-xs text-greyscale-text-caption">
        Centre: {latitude.toFixed(5)}, {longitude.toFixed(5)} · Radius: {radiusMeters}m
      </div>
    </div>
  );
}
